import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useApi, errMsg } from "@/lib/api";
import { fmtDT } from "@/lib/brand";
import { Trash2, Pencil } from "lucide-react";

export default function AdminCustomers() {
  const api = useApi();
  const [data, setData] = useState({ roles: [], users: [] });
  const load = useCallback(() => {
    api.get("/users").then((r) => setData(r.data));
  }, [api]);
  useEffect(() => {
    load();
  }, [load]);
  const setRole = async (uid, role) => {
    try { await api.patch(`/users/${uid}/role`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const fixPhone = async (u) => {
    const v = window.prompt(`Correct mobile number for ${u.name || u.email} (10 digits). They will sign in with this number.`, u.phone_verified || "");
    if (v === null || v.trim() === (u.phone_verified || "")) return;
    try { await api.patch(`/users/${u.user_id}/phone`, { phone: v }); toast.success("Mobile number updated"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name || u.email}'s account? Any ongoing trip is ended now and any upcoming booking is cancelled (refund marked pending). All booking history and invoices are kept.`)) return;
    try { await api.delete(`/users/${u.user_id}?end_trips=true`); toast.success("Account deleted. Booking history kept"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6" data-testid="admin-customers">
      <h1 className="text-3xl font-semibold tracking-tight">Customers <span className="text-neutral-500">({data.users.length})</span></h1>
      <div className="overflow-x-auto rounded-lg border border-white/10 bg-[#141414]">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr>{["Name", "Email", "Mobile", "Joined", "Bookings", "Role", ""].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {data.users.map((u) => (
              <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}>
                <td className="px-4 py-3 font-medium">{u.name || "—"}</td>
                <td className="px-4 py-3 text-neutral-400">{u.email}</td>
                <td className="px-4 py-3 text-neutral-400" data-testid={`admin-user-phone-${u.user_id}`}>
                  <button onClick={() => fixPhone(u)} data-testid={`admin-user-phone-edit-${u.user_id}`} className="group inline-flex items-center gap-1.5 hover:text-white" title="Edit mobile number">
                    {u.phone_verified || "—"}<Pencil className="h-3 w-3 opacity-40 group-hover:opacity-100" />
                  </button>
                </td>
                <td className="px-4 py-3 text-neutral-400">{fmtDT(u.created_at)}</td>
                <td className="px-4 py-3">{u.bookings}</td>
                <td className="px-4 py-3">
                  <select value={u.role} onChange={(e) => setRole(u.user_id, e.target.value)} data-testid={`admin-user-role-${u.user_id}`} className="rounded-md border border-white/10 bg-[#0A0A0A] px-2 py-1 text-sm capitalize">
                    {data.roles.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-right">
                  {u.role !== "admin" && (
                  <button onClick={() => remove(u)} data-testid={`admin-user-delete-${u.user_id}`} className="rounded-md p-1.5 text-neutral-500 transition-colors hover:bg-red-500/10 hover:text-red-400" title="Delete account">
                    <Trash2 className="h-4 w-4" />
                  </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
